import { Injectable } from '@angular/core';
import {
  GetInstrumentDetailsQuery,
  GetInstrumentDetailsQueryVariables,
  GetInstrumentLastPriceQuery,
  GetInstrumentLastPriceQueryVariables
} from '@six-api';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Instrument } from '@qvs/core';
import { InstrumentMapper } from '~/modules/instrument/instrument.mapper';
import { map } from 'rxjs/operators';
import { isinBcMap } from '~/modules/instrument/isinBcMap';
import { isNil } from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class SixMarketDataService {
  public baseUrl: string;
  private headers = new HttpHeaders({
    //eslint-disable-next-line @typescript-eslint/naming-convention
    Accept: 'application/json'
  });

  constructor(private readonly httpClient: HttpClient) {}

  public loadInstrumentLastPrice = (instruments: Instrument[]) => {
    const isins = instruments.map(this.getIsinBc).filter((isinBc) => !isNil(isinBc));
    return this.queryInstrumentLastPrice({ isins }).pipe(
      map((response) => InstrumentMapper.fromSixInstrumentLastPrice(response, instruments))
    );
  };

  public loadInstrumentDetails = (instrument: Instrument, dateFrom: string) =>
    this.queryInstrumentDetails({ isin: this.getIsinBc(instrument), dateFrom }).pipe(
      map((response) => InstrumentMapper.fromSixInstrumentDetails(response, instrument))
    );

  private getIsinBc = (instrument: Instrument) => {
    const bc = isinBcMap.get(instrument.isin);
    return !isNil(bc) ? `${instrument.isin}_${isinBcMap.get(instrument.isin)}` : null;
  };

  private readonly queryInstrumentLastPrice = (input: GetInstrumentLastPriceQueryVariables) => {
    const operationName = 'GetInstrumentLastPrice';
    const query = `
	query GetInstrumentLastPrice($isins: [UserInputId!]!) {
		listings(scheme: ISIN_BC, ids: $isins) {
			requestedId
			lookupStatus
			lookup {
			    listingName
				listingCurrency
			}
			marketData {
				intradaySnapshot {
					last {
						...ValSize
					}
				}
			}
		}
	}
	fragment ValSize on ValueAndSize {
        value
        timestamp
    }
	`;
    return this.query<GetInstrumentLastPriceQuery>(operationName, query, input);
  };

  private readonly queryInstrumentDetails = (input: GetInstrumentDetailsQueryVariables) => {
    const operationName = 'GetInstrumentDetails';
    const query = `
    query GetInstrumentDetails($isin: UserInputId!, $dateFrom: LocalDate!) {
        listings(scheme: ISIN_BC, ids: [$isin]) {
            lookupStatus
            lookup {
                listingCurrency
            }
            marketData {
                intradaySnapshot {
                    yieldToMaturity {
                        ...Val
                    }
                    priceEarningsRatioEstimated {
                        ...Val
                    }
                    marketCapitalisation {
                        ...Val
                    }
                    turnover {
                        ...Val
                    }
                }
                intradayHistory {
                    summary(filters: {dateFrom: $dateFrom, period: "PT5M"}) {
                        intervalFrom
                        last
                    }
                }
            }
        }
    }
    fragment Val on Value {
        value
        timestamp
    }
    `;
    return this.query<GetInstrumentDetailsQuery>(operationName, query, input);
  };

  private query = <Q>(operationName: string, query: string, input: object) => {
    const request = {
      query,
      variables: {
        ...input
      },
      operationName
    };
    return this.httpClient
      .post<GraphQlResponse<Q>>(this.baseUrl, request, { withCredentials: true, headers: this.headers })
      .pipe(map((response) => response.data));
  };
}

interface GraphQlResponse<Q> {
  data: Q;
}
